/* ============================================
   CENTER ORB COMPONENT
   The Creative Orchestra - GEMINI Framework
   The central novel orb in the orbital system
   ============================================ */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, Sparkles } from 'lucide-react';
import { chapters } from '@data';

/**
 * Center Orb Component
 * The central, larger orb representing the novel
 * Features pulsing animation and prominent styling
 * 
 * @param {Object} props
 * @param {number} props.size - Orb size in pixels (default: 140)
 */
const CenterOrb = ({ size = 140 }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div 
      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10"
      style={{ width: size, height: size }}
    >
      <Link
        to="/novel"
        className="block relative w-full h-full group"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Outer glow rings */}
        <motion.div
          className="absolute inset-0 rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(129, 140, 248, 0.2) 0%, transparent 70%)',
            transform: 'scale(2)',
          }}
          animate={{
            opacity: [0.5, 0.8, 0.5],
            scale: [1.8, 2.2, 1.8],
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />

        {/* Secondary pulse ring */}
        <motion.div
          className="absolute inset-0 rounded-full border border-indigo-400/30"
          animate={{
            scale: [1, 1.5, 1],
            opacity: [0.6, 0, 0.6],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: 'easeOut',
          }}
        />

        {/* Tertiary pulse ring */}
        <motion.div
          className="absolute inset-0 rounded-full border border-purple-400/20"
          animate={{
            scale: [1, 1.8, 1],
            opacity: [0.4, 0, 0.4],
          }}
          transition={{
            duration: 3,
            delay: 0.5,
            repeat: Infinity,
            ease: 'easeOut',
          }}
        />

        {/* Hover ring */}
        <motion.div
          className="absolute inset-0 rounded-full border-2 border-white/40"
          initial={{ scale: 1, opacity: 0 }}
          animate={{
            scale: isHovered ? 1.2 : 1,
            opacity: isHovered ? 1 : 0,
          }}
          transition={{ duration: 0.3 }}
        />

        {/* Main orb */}
        <motion.div
          className="relative w-full h-full rounded-full flex flex-col items-center justify-center
            cursor-pointer overflow-hidden border-2 border-white/30"
          style={{
            background: `
              radial-gradient(circle at 30% 20%, rgba(255,255,255,0.15) 0%, transparent 50%),
              linear-gradient(135deg, 
                rgba(129, 140, 248, 0.4) 0%, 
                rgba(168, 85, 247, 0.3) 50%, 
                rgba(129, 140, 248, 0.4) 100%
              )
            `,
            boxShadow: `
              0 0 40px rgba(129, 140, 248, 0.4),
              0 0 80px rgba(168, 85, 247, 0.2),
              inset 0 0 40px rgba(255, 255, 255, 0.1)
            `,
          }}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          animate={{
            boxShadow: isHovered
              ? `
                0 0 60px rgba(129, 140, 248, 0.6),
                0 0 100px rgba(168, 85, 247, 0.4),
                inset 0 0 40px rgba(255, 255, 255, 0.15)
              `
              : `
                0 0 40px rgba(129, 140, 248, 0.4),
                0 0 80px rgba(168, 85, 247, 0.2),
                inset 0 0 40px rgba(255, 255, 255, 0.1)
              `,
          }}
          transition={{ duration: 0.3 }}
        >
          {/* Rotating inner ring */}
          <motion.div
            className="absolute inset-4 rounded-full border border-white/10"
            animate={{ rotate: 360 }}
            transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
          />

          {/* Icon */}
          <motion.div
            animate={{ 
              y: isHovered ? -2 : 0,
            }}
            transition={{ duration: 0.3 }}
          >
            <BookOpen className="w-8 h-8 text-white mb-1" />
          </motion.div>

          {/* Label */}
          <motion.span 
            className="text-white text-sm font-semibold tracking-wide"
            animate={{
              y: isHovered ? 2 : 0,
            }}
            transition={{ duration: 0.3 }}
          >
            NOVEL
          </motion.span>

          {/* Sparkle decorations */}
          <Sparkles className="absolute top-3 right-3 w-3 h-3 text-white/50" />
          <Sparkles className="absolute bottom-4 left-4 w-2 h-2 text-white/30" />
        </motion.div>

        {/* Tooltip */}
        <motion.div
          className="absolute left-1/2 -translate-x-1/2 pointer-events-none
            whitespace-nowrap px-4 py-2 rounded-xl
            bg-black/80 backdrop-blur-sm border border-white/10
            text-center z-20"
          style={{ top: size + 15 }}
          initial={{ opacity: 0, y: -10 }}
          animate={{ 
            opacity: isHovered ? 1 : 0, 
            y: isHovered ? 0 : -10,
          }}
          transition={{ duration: 0.2 }}
        >
          <p className="text-white font-medium">The Creative Orchestra</p>
          <p className="text-gray-400 text-xs mt-1">
            {chapters.totalChapters} Chapters • {chapters.estimatedReadingTime}
          </p>
        </motion.div>
      </Link>

      {/* Floating particles around orb */}
      <OrbParticles size={size} />
    </div>
  );
};

/**
 * Floating Particles Component
 * Small particles orbiting around the center orb
 */
const OrbParticles = ({ size }) => {
  const particles = [
    { angle: 0, distance: 1.2, duration: 8, delay: 0 },
    { angle: Math.PI / 3, distance: 1.4, duration: 10, delay: 1 },
    { angle: Math.PI * 2/3, distance: 1.3, duration: 9, delay: 2 },
    { angle: Math.PI, distance: 1.5, duration: 11, delay: 0.5 },
    { angle: Math.PI * 4/3, distance: 1.25, duration: 8.5, delay: 1.5 },
    { angle: Math.PI * 5/3, distance: 1.35, duration: 9.5, delay: 2.5 },
  ];

  return (
    <>
      {particles.map((particle, index) => (
        <motion.div
          key={index}
          className="absolute w-1.5 h-1.5 rounded-full bg-white/60"
          style={{
            left: '50%',
            top: '50%',
            marginLeft: -3,
            marginTop: -3,
          }}
          animate={{
            x: [
              Math.cos(particle.angle) * size * particle.distance * 0.5,
              Math.cos(particle.angle + Math.PI) * size * particle.distance * 0.5,
              Math.cos(particle.angle + Math.PI * 2) * size * particle.distance * 0.5,
            ],
            y: [
              Math.sin(particle.angle) * size * particle.distance * 0.5,
              Math.sin(particle.angle + Math.PI) * size * particle.distance * 0.5,
              Math.sin(particle.angle + Math.PI * 2) * size * particle.distance * 0.5,
            ],
            opacity: [0.6, 0.3, 0.6],
          }}
          transition={{
            duration: particle.duration,
            delay: particle.delay,
            repeat: Infinity,
            ease: 'linear',
          }}
        />
      ))}
    </>
  );
};

/**
 * Mini Center Orb (for smaller displays)
 */
export const MiniCenterOrb = ({ size = 60, className = '' }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <Link
      to="/novel"
      className={`relative inline-block ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <motion.div
        className="rounded-full flex items-center justify-center
          cursor-pointer border border-white/30"
        style={{
          width: size,
          height: size,
          background: `
            linear-gradient(135deg, 
              rgba(129, 140, 248, 0.4) 0%, 
              rgba(168, 85, 247, 0.3) 100%
            )
          `,
          boxShadow: '0 0 20px rgba(129, 140, 248, 0.3)',
        }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
      >
        <BookOpen 
          className="text-white" 
          style={{ width: size * 0.35, height: size * 0.35 }}
        />
      </motion.div>
    </Link>
  );
};

export default CenterOrb;
